import React, { useState, useEffect } from 'react';
import {
  X,
  Check,
  ChefHat,
  Printer,
  User,
  Phone,
  Loader2,
  QrCode,
  Bike,
  Package,
  Bell,
  Clock,
  ShoppingBag,
  MessageSquare,
  AlertTriangle,
  RefreshCw,
  Send,
} from 'lucide-react';
import toast from 'react-hot-toast';
import useQROrders from '../../../hooks/useQROrders';
import useBranch from '../../../hooks/useBranch';
import qrOrderService from '../../../services/qrOrderService';
import QRKOTPreview from '../../QRMenu/QRKOTPreview';
import printService from '../../../services/printService';
import receiptConfigurationService from '../../../services/receiptConfigurationService';
import branchService from '../../../services/branchService';
import { generateKOTContent } from '../../../utils/receiptFormatter';
import { useAuth } from '../../../context/AuthContext';
import { P } from '../../../config/permissionCodes';

// ─── Channel tabs ────────────────────────────────────────────────────────────
const CHANNELS = [
  { id: 'qr', label: 'QR Dine-in', icon: QrCode },
  { id: 'takeaway', label: 'Takeaway', icon: Package, disabled: true },
  { id: 'delivery', label: 'Delivery', icon: Bike, disabled: true },
];

// ─── Time helper ─────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}

function urgencyColor(dateStr) {
  if (!dateStr) return 'text-slate-400';
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 3) return 'text-emerald-500';
  if (mins < 10) return 'text-amber-500';
  return 'text-red-500';
}

// ═══════════════════════════════════════════════════════════════════════════════
// ORDER CARD
// ═══════════════════════════════════════════════════════════════════════════════
function OrderCard({ order, onSendKOT, onReject, shouldPrintKOT, canSendKitchen }) {
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [sending, setSending] = useState(false);
  const [submittingReject, setSubmittingReject] = useState(false);

  const isProcessing = sending;

  const handleSendKOT = async () => {
    setSending(true);
    try { await onSendKOT(order.id); } finally { setSending(false); }
  };

  const handleReject = async () => {
    setSubmittingReject(true);
    try {
      await onReject(order.id, rejectReason || 'Rejected by staff');
      setRejecting(false);
      setRejectReason('');
    } finally { setSubmittingReject(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* ── Card Header ─────────────────────────────────────────────────── */}
      <div className="px-4 py-3 bg-gradient-to-r from-teal-50 to-slate-50 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-1.5">
            <ShoppingBag size={13} className="text-teal-500" />
            <span className="text-xs font-bold text-slate-900">#{order.id}</span>
          </div>
          <span className="text-[10px] text-slate-300">|</span>
          <span className="text-[11px] font-semibold text-teal-700">{order.tableName || 'Table'}</span>
        </div>
        <div className={`flex items-center gap-1 text-[11px] font-medium ${urgencyColor(order.createdAt)}`}>
          <Clock size={11} />
          {timeAgo(order.createdAt)}
        </div>
      </div>

      {/* ── Customer Info ───────────────────────────────────────────────── */}
      {(order.customerName || order.customerPhone) && (
        <div className="px-4 py-2 flex items-center gap-4 bg-slate-50/60 border-b border-slate-100/60">
          {order.customerName && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <User size={11} className="text-slate-400" /> {order.customerName}
            </span>
          )}
          {order.customerPhone && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <Phone size={11} className="text-slate-400" /> {order.customerPhone}
            </span>
          )}
        </div>
      )}

      {/* ── Items ───────────────────────────────────────────────────────── */}
      <div className="px-4 py-3">
        <table className="w-full">
          <thead>
            <tr className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
              <th className="text-left pb-1.5">Item</th>
              <th className="text-center pb-1.5 w-10">Qty</th>
              <th className="text-right pb-1.5 w-16">Price</th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map((item, i) => (
              <tr key={i} className="border-t border-slate-50">
                <td className="py-1.5">
                  <span className="text-[11px] font-medium text-slate-800">{item.itemName}</span>
                  {item.variantName && (
                    <span className="text-[10px] text-slate-400 ml-1">({item.variantName})</span>
                  )}
                  {item.specialInstructions && (
                    <p className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-0.5">
                      <MessageSquare size={8} /> {item.specialInstructions}
                    </p>
                  )}
                </td>
                <td className="text-center text-[11px] font-semibold text-slate-600">{item.quantity}</td>
                <td className="text-right text-[11px] text-slate-600">{Number(item.totalPrice).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Notes */}
        {order.notes && (
          <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
            <p className="text-[10px] font-medium text-amber-700">Note: {order.notes}</p>
          </div>
        )}

        {/* Total */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
          <span className="text-[11px] text-slate-400">{order.totalItems} item{order.totalItems !== 1 ? 's' : ''}</span>
          <span className="text-sm font-bold text-slate-900">{Number(order.totalAmount).toFixed(2)}</span>
        </div>
      </div>

      {/* ── Action Buttons ──────────────────────────────────────────────── */}
      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40">
        {rejecting ? (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs text-red-600">
              <AlertTriangle size={13} />
              <span className="font-semibold">Reject this order?</span>
            </div>
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)"
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-red-300 focus:ring-2 focus:ring-red-50"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleReject()}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleReject}
                disabled={submittingReject}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {submittingReject ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                Confirm Reject
              </button>
              <button
                onClick={() => { setRejecting(false); setRejectReason(''); }}
                className="px-4 py-2 text-xs font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {/* Send KOT — only shown if user has SEND_KITCHEN permission */}
              {canSendKitchen ? (
                <button
                  onClick={handleSendKOT}
                  disabled={isProcessing}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 transition-colors shadow-sm"
                >
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  Send KOT
                </button>
              ) : (
                <div className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold rounded-lg bg-slate-100 text-slate-400 cursor-not-allowed">
                  <Send size={13} />
                  Send KOT
                </div>
              )}
            </div>
            <div className="pt-1 border-t border-slate-100">
              <button
                onClick={() => setRejecting(true)}
                disabled={isProcessing}
                className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 text-[11px] font-medium text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-40 transition-colors"
              >
                <X size={11} />
                Reject Order
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN MODAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function IncomingOrdersModal({ onClose, onOrderApproved }) {
  const { hasPermission } = useAuth();

  // ─── Permission flags ──────────────────────────────────────────────────────
  const canSendKitchen = hasPermission(P.POS.DINE_IN.INCOMING.SEND_KITCHEN);
  const canPrintKOT    = hasPermission(P.POS.DINE_IN.INCOMING.PRINT_KOT);
  // ──────────────────────────────────────────────────────────────────────────

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const {
    pendingOrders,
    approveOrder,
    rejectOrder,
    refreshPending,
  } = useQROrders({ autoRefresh });

  const { branch } = useBranch();
  const kotModes = branch?.kotModes || ['PRINT'];
  // Only print if branch has PRINT mode AND user has PRINT_KOT permission
  const shouldPrintKOT = kotModes.includes('PRINT') && canPrintKOT;

  const SHOW_KOT_PREVIEW = false;

  const [activeChannel, setActiveChannel] = useState('qr');
  const [kotPreviewOrder, setKotPreviewOrder] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try { await refreshPending(); } finally { setRefreshing(false); }
  };

  const directPrintQRKOT = async (order) => {
    try {
      const [receiptConfig, branchData] = await Promise.all([
        receiptConfigurationService.getDefault('KOT').catch(() => null),
        branchService.getCurrentBranch().catch(() => null),
      ]);
      const formattedBranch = branchData ? branchService.formatBranchForReceipt(branchData) : null;

      const grouped = {};
      (order.items || []).forEach(item => {
        const station = item.kitchenStationName || 'MAIN KITCHEN';
        if (!grouped[station]) grouped[station] = [];
        grouped[station].push(item);
      });

      const stations = Object.keys(grouped);
      const timestamp = new Date().toLocaleString('en-IN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });

      const printPromises = stations.map(async (station) => {
        const kotPrintData = {
          restaurantName: formattedBranch?.name || 'Restaurant',
          kotNumber: order.kotNumber || 'N/A',
          tableName: order.tableName || 'N/A',
          tableSplit: order.tableSplit || 'A',
          pax: null,
          stationName: station,
          waiterName: null,
          timestamp,
          items: grouped[station].map(item => ({
            name: item.itemName,
            quantity: item.quantity,
            customization: null,
          })),
          chefNotes: order.notes,
          allergies: null,
        };

        const kotResult = generateKOTContent(receiptConfig || {}, kotPrintData);

        return printService.printKOTToStation(station, {
          formattedContent: kotResult.content,
          printType: 'KOT',
          kitchenStationName: station,
          content: {
            kotNumber: kotPrintData.kotNumber,
            tableName: kotPrintData.tableName,
            tableSplit: kotPrintData.tableSplit,
            stationName: station,
            timestamp: kotPrintData.timestamp,
            items: kotPrintData.items,
            chefNotes: kotPrintData.chefNotes,
          },
        });
      });

      const results = await Promise.all(printPromises);
      const allSuccess = results.every(r => r?.success);
      if (allSuccess) {
        toast.success(`KOT printed to ${stations.length} station${stations.length > 1 ? 's' : ''}`, { duration: 2000 });
      } else {
        toast.error('Some KOT prints failed', { duration: 3000 });
      }
    } catch (error) {
      console.error('Direct KOT print error:', error);
      toast.error('KOT print failed');
    }
  };

  // Unified handler: approve order, then conditionally print KOT
  const handleSendKOT = async (id) => {
    if (!canSendKitchen) {
      toast.error('You do not have permission to send orders to the kitchen', { icon: '🚫' });
      return;
    }
    try {
      const result = await qrOrderService.approve(id);
      // Only print if user also has PRINT_KOT permission and branch supports it
      if (result && shouldPrintKOT) {
        if (SHOW_KOT_PREVIEW) {
          setKotPreviewOrder(result);
        } else {
          await directPrintQRKOT(result);
        }
      }
      toast.success('Order approved - sent to kitchen');
      refreshPending();
      if (onOrderApproved) onOrderApproved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to approve order');
    }
  };

  const orders = activeChannel === 'qr' ? pendingOrders : [];

  return (
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden">

          {/* ═══ Header ═══════════════════════════════════════════════════ */}
          <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-6 py-4 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-white/15 rounded-xl flex items-center justify-center">
                  <Bell size={22} className="text-teal-100" />
                </div>
                <div>
                  <h2 className="text-lg font-bold tracking-tight">Incoming Orders</h2>
                  <p className="text-xs text-teal-200 mt-0.5">
                    {pendingOrders.length > 0
                      ? `${pendingOrders.length} order${pendingOrders.length !== 1 ? 's' : ''} awaiting action`
                      : 'No pending orders'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/15 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* ═══ Channel Tabs ═══════════════════════════════════════════ */}
            <div className="flex items-center gap-1 mt-4 bg-teal-900/40 rounded-xl p-1">
              {CHANNELS.map((ch) => {
                const Icon = ch.icon;
                const isActive = activeChannel === ch.id;
                const count = ch.id === 'qr' ? pendingOrders.length : 0;
                return (
                  <button
                    key={ch.id}
                    onClick={() => !ch.disabled && setActiveChannel(ch.id)}
                    disabled={ch.disabled}
                    className={`
                      relative flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all
                      ${isActive
                        ? 'bg-white text-teal-800 shadow-lg'
                        : ch.disabled
                          ? 'text-teal-500/60 cursor-not-allowed'
                          : 'text-teal-200 hover:text-white hover:bg-white/10'
                      }
                    `}
                  >
                    <Icon size={14} />
                    {ch.label}
                    {count > 0 && isActive && (
                      <span className="ml-1 min-w-[18px] h-[18px] bg-teal-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                        {count}
                      </span>
                    )}
                    {ch.disabled && (
                      <span className="text-[9px] font-normal text-teal-400/60 ml-0.5">Soon</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ═══ Content ═══════════════════════════════════════════════════ */}
          <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50/50">
            {orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-4">
                  <Check size={28} className="text-teal-500" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700 mb-1">All caught up!</h3>
                <p className="text-xs text-slate-400 mb-4">
                  {autoRefresh ? 'New orders will appear here automatically' : 'Click refresh to check for new orders'}
                </p>
                {autoRefresh && (
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                    <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse" />
                    Listening for orders...
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onSendKOT={handleSendKOT}
                    onReject={rejectOrder}
                    shouldPrintKOT={shouldPrintKOT}
                    canSendKitchen={canSendKitchen}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ═══ Footer ═══════════════════════════════════════════════════ */}
          <div className="px-6 py-2.5 bg-white border-t border-slate-200 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-8 h-[18px] bg-slate-300 rounded-full peer-checked:bg-teal-500 transition-colors" />
                  <div className="absolute top-[2px] left-[2px] w-[14px] h-[14px] bg-white rounded-full shadow-sm peer-checked:translate-x-[14px] transition-transform" />
                </div>
                <span className="text-[11px] font-medium text-slate-500">Auto-refresh</span>
              </label>
              {autoRefresh && (
                <div className="flex items-center gap-1.5 text-[10px] text-teal-500">
                  <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse" />
                  Every 5s
                </div>
              )}
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
            <button
              onClick={onClose}
              className="px-5 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* KOT Preview Modal */}
      {kotPreviewOrder && (
        <QRKOTPreview
          order={kotPreviewOrder}
          onClose={() => setKotPreviewOrder(null)}
        />
      )}
    </>
  );
}